/** EMPLOYEE PORTAL — lightweight, passwordless (OTP / magic link). Route: /me */
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, LogOut, ShieldCheck, Home, Laptop2 } from "lucide-react";
import { EP } from "./employeePortalParts";
import EmployeePortalLogin from "./EmployeePortalLogin";
import { Equipment, Delivery, Support, Returns } from "./employeePortalSections";
import MyDevicesScreen from "./MyDevicesScreen";

export default function EmployeePortal() {
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState("checking");
  const [home, setHome] = useState(null);
  const [magicError, setMagicError] = useState(null);
  const [tab, setTab] = useState(() => (params.get("tab") === "devices" ? "devices" : "home"));

  const load = useCallback(async () => {
    try { const { data } = await EP.get("/home"); setHome(data); setState("home"); }
    catch { setHome(null); setState("login"); }
  }, []);

  const magicRef = useRef(null);
  useEffect(() => {
    const token = params.get("token");
    if (token) {
      if (!magicRef.current || magicRef.current.token !== token) {
        magicRef.current = { token, promise: EP.post("/auth/magic/verify", { token }) };
      }
      magicRef.current.promise.then(() => { setParams({}, { replace: true }); load(); })
        .catch((e) => { setMagicError(e.response?.data?.detail || "This sign-in link is invalid or has expired"); setParams({}, { replace: true }); setState("login"); });
    } else if (!magicRef.current) load();
  }, [params, setParams, load]);

  const logout = async () => { try { await EP.post("/auth/logout"); } catch { /* ignore */ } setHome(null); setState("login"); };

  if (state === "checking") return <div className="min-h-[100dvh] flex items-center justify-center bg-[#F4F6FA]" data-testid="ep-checking"><Loader2 className="h-6 w-6 animate-spin text-[#0B1730]" /></div>;
  if (state === "login") return <EmployeePortalLogin onAuthed={load} magicError={magicError} />;

  const e = home.employee;
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  return (
    <div className="min-h-[100dvh] bg-[#F4F6FA]" data-testid="employee-portal">
      <header style={{ background: "#0B1730", color: "#fff" }}>
        <div className="max-w-2xl mx-auto px-4 py-5 flex items-start gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold inline-flex items-center gap-1" style={{ color: "rgba(255,255,255,0.65)" }}><ShieldCheck className="h-3 w-3" /> Employee Portal{e.company ? ` · ${e.company}` : ""}</div>
            <h1 className="text-2xl sm:text-3xl font-bold mt-1 leading-tight" style={{ color: "#fff" }} data-testid="ep-welcome">WELCOME {String(e.name || "").toUpperCase()}</h1>
            <p className="text-[13px] mt-0.5" style={{ color: "rgba(255,255,255,0.75)" }}>{greet}{e.department ? ` · ${e.department}` : ""}</p>
          </div>
          <button onClick={logout} className="ml-auto shrink-0 inline-flex items-center gap-1 text-[12px] font-medium rounded-full px-3 h-8" style={{ color: "#fff", background: "rgba(255,255,255,0.14)" }} data-testid="ep-logout"><LogOut className="h-3.5 w-3.5" /> Sign out</button>
        </div>
      </header>
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-20" data-testid="ep-tabs">
        <div className="max-w-2xl mx-auto px-4 flex">
          {[["home", "Home", Home], ["devices", "My Devices", Laptop2]].map(([k, l, I]) => (
            <button key={k} onClick={() => setTab(k)} className={`h-12 px-4 inline-flex items-center gap-1.5 text-[13px] font-semibold border-b-2 -mb-px ${tab === k ? "border-[#0B1730] text-[#0B1730]" : "border-transparent text-slate-500"}`} data-testid={`ep-tab-${k}`}><I className="h-4 w-4" /> {l}{k === "devices" && home.returns?.open_recovery_id ? <span className="ml-1 h-2 w-2 rounded-full bg-rose-500" /> : null}</button>
          ))}
        </div>
      </nav>
      <main className="max-w-2xl mx-auto px-4 py-5 space-y-4 pb-12">
        {tab === "devices" ? <MyDevicesScreen onChange={load} /> : <>
        <Equipment items={home.equipment} onOpenDevices={() => setTab("devices")} />
        <Delivery d={home.delivery} onChange={load} />
        <Support s={home.support} equipment={home.equipment} onChange={load} />
        <Returns r={home.returns} onChange={load} />
        </>}
        <p className="text-center text-[11px] text-slate-400 pt-2">Powered by SUNTRONIX ONE · Only your own records are shown.</p>
      </main>
    </div>
  );
}
